<script lang="ts">
    import { createEventDispatcher, onDestroy, onMount, tick } from 'svelte';
    import { replace } from 'svelte-spa-router';

    import closePhoneIcon from '../assets/close_phone.png';
    import countryIcon from '../assets/country_icon.png';
    import { CREDIT_CARD_PROTOCOL } from '../constants/common';
    import { COOKIE_SCROLL_DURATION_MS } from '../constants/cookieLayout';
    import {
        INPUT_CLOSE_ICON,
        INPUT_TIPS_ERROR_ICON,
        INPUT_TIPS_NORMAL_ICON
    } from '../constants/loginImage';
    import { JUMP_TYPE_ENUM, POPUP_ID } from '../enums';
    import {
        MODULE_ENUM as LEGACY_MODULE_ENUM,
        type LoginModuleType as LegacyLoginModuleType
    } from '../enums/loginModule';
    import {
        LOGIN_STATE_CK,
        PAGE_CLICK_CK,
        PHONE_VERIFY_SW,
        REG_PHONE_INPUT_SW
    } from '../enums/track';
    import {
        cookieHeight,
        isJumpOneLink,
        isLoading,
        isLogin,
        PhoneCell,
        startTime
    } from '../store/index';
    import * as legacyLoginFlow from '../store/loginFlow';
    import { mediaEventUpOnce } from '../utils/eventUp';
    import { JumpLink } from '../utils/jumpTo';
    import { handleLandingCtaClick } from '../utils/landingCta';
    import {
        checkPhoneApi,
        genSliderCaptchaApi,
        getPassportError
    } from '../utils/loginPassport';
    import { usesFifaCardLogin } from '../utils/pageType';
    import { initPassportSDK, isPassportSdkReady } from '../utils/passport';
    import { trackEvent } from '../utils/track';

    import webp from '@/actions/webp';
    import {
        MODULE_ENUM as CARD_LOGIN_MODULE_ENUM,
        type LoginModuleType as CardLoginModuleType
    } from '@/components/cardLogin/fifa/enums/loginModule';
    import * as cardLoginFlow from '@/components/cardLogin/fifa/store/loginFlow';
    import { debounce } from '@/utils/debounce';
    import { markPopBegin, pageLeave } from '@/utils/fmpTrack';
    import url from '@/utils/url';

    export let loginType: 'default' | 'E' = 'default';
    export let confirmButton: HTMLDivElement | undefined = undefined;
    export let buttonLoading = false;

    $: useCardLoginFifa = usesFifaCardLogin();

    const dispatch = createEventDispatcher<{
        inputErrorChange: boolean;
    }>();
    let scrollElement: HTMLElement;
    let scrollAnimFrame: number | null = null;
    let sliderCaptchaInstance: { destroy?: () => void } | undefined;

    const cancelCookieScrollAnim = () => {
        if (scrollAnimFrame !== null) {
            cancelAnimationFrame(scrollAnimFrame);
            scrollAnimFrame = null;
        }
    };

    const scrollByLinear = (deltaY: number) => {
        if (deltaY <= 0.5) return;

        cancelCookieScrollAnim();
        const startY = window.scrollY;
        const endY = startY + deltaY;
        const t0 = performance.now();

        const step = (now: number) => {
            const t = Math.min(1, (now - t0) / COOKIE_SCROLL_DURATION_MS);
            const y = startY + (endY - startY) * t;

            window.scrollTo(0, y);

            if (t < 1) {
                scrollAnimFrame = requestAnimationFrame(step);
            } else {
                scrollAnimFrame = null;
            }
        };

        scrollAnimFrame = requestAnimationFrame(step);
    };

    const MAX_LENGTH = url.get('is_test') ? 11 : 10;
    let phoneDigits = '';
    let isInputError = false;
    let tipsMessage = '';
    let phoneInputEl: HTMLInputElement;
    let isInputFocused = false;
    let hasTypedAnyDigit = false;
    let lastPhoneInputBlurReportedDigits = '';
    /** 点 X 清空：按钮立即高亮，再次点击下一步才置灰并展示报错 */
    let keepConfirmBtnHighlighted = false;

    const reportPhoneInputBlurDebounced = debounce(() => {
        if (!hasTypedAnyDigit && phoneDigits.length === 0) return;
        if (phoneDigits === lastPhoneInputBlurReportedDigits) return;
        lastPhoneInputBlurReportedDigits = phoneDigits;

        trackEvent(REG_PHONE_INPUT_SW, {
            is_phone_input_full: phoneDigits.length === MAX_LENGTH ? 1 : 0,
            is_error: isInputError ? 1 : 0
        });
    }, 800);

    const handleTipsMessage = (isError: boolean, message: string) => {
        isInputError = isError;
        tipsMessage = message;
    };

    const formatPhone = (digits: string) => {
        return [digits.slice(0, 2), digits.slice(2, 6), digits.slice(6, MAX_LENGTH)]
            .filter(Boolean)
            .join(' ');
    };

    const onPhoneInputFormat = (
        inputEl: HTMLInputElement,
        originalValue: string
    ) => {
        const originalCaret = inputEl?.selectionStart ?? originalValue.length;
        const digitsBeforeCaret = originalValue
            .slice(0, originalCaret)
            .replace(/\D/g, '').length;
        const digits = originalValue.replace(/\D/g, '').slice(0, MAX_LENGTH);
        const formatted = formatPhone(digits);

        phoneDigits = digits;
        PhoneCell.set(digits);
        inputEl.value = formatted;
        handleTipsMessage(false, '');

        let newCaret = 0;

        if (digitsBeforeCaret > 0) {
            let seenDigits = 0;

            for (let i = 0; i < formatted.length; i++) {
                if (/\d/.test(formatted[i])) {
                    seenDigits++;
                    if (seenDigits === digitsBeforeCaret) {
                        newCaret = i + 1;
                        break;
                    }
                }
            }

            if (newCaret === 0) newCaret = formatted.length;
        }

        inputEl.setSelectionRange(newCaret, newCaret);
    };

    const handleInput = (e: Event) => {
        const el = e.currentTarget as HTMLInputElement;

        keepConfirmBtnHighlighted = false;
        onPhoneInputFormat(el, el.value);
        hasTypedAnyDigit = phoneDigits.length > 0;
    };

    const focusPhoneInput = () => {
        if (isInputFocused) return;
        isInputFocused = true;
        phoneInputEl?.focus();
    };

    const handlePhoneInputClick = () => {
        trackEvent(PAGE_CLICK_CK, {
            button_name: 'phone_number_input',
            is_phone_input_full: phoneDigits.length === MAX_LENGTH ? 1 : 0
        });
    };

    const handleBlurInput = () => {
        if (!isInputFocused) return;

        isInputFocused = false;

        if (phoneDigits.length > 0) {
            if (phoneDigits.length < MAX_LENGTH || isAllZeroPhone(phoneDigits)) {
                if (!keepConfirmBtnHighlighted) {
                    showInvalidPhoneError();
                }
            } else {
                handleTipsMessage(false, '');
            }
        }

        reportPhoneInputBlurDebounced();
    };

    const handleEmptyInput = () => {
        if (!phoneInputEl) return;

        trackEvent(PAGE_CLICK_CK, {
            button_name: 'close',
            is_phone_input_full: phoneDigits.length === MAX_LENGTH ? 1 : 0
        });
        keepConfirmBtnHighlighted = true;

        phoneDigits = '';
        PhoneCell.set('');
        phoneInputEl.value = '';
        handleTipsMessage(false, '');
        isInputFocused = true;
        phoneInputEl?.focus();
    };

    const isAllZeroPhone = (digits: string) => {
        if (!digits) return false;

        return Number(digits) === 0;
    };

    const showInvalidPhoneError = () => {
        handleTipsMessage(
            true,
            'Por favor, ingresa tu número de celular completo'
        );
    };

    $: isPhoneValid =
        phoneDigits.length === MAX_LENGTH && !isAllZeroPhone(phoneDigits);
    $: shouldShowProgress = phoneDigits.length > 0;
    $: confirmBtnHighlighted = (() => {
        if ($isLogin) return true;
        if (isPhoneValid) return true;
        if (keepConfirmBtnHighlighted) return true;

        return phoneDigits.length === 0 && !hasTypedAnyDigit;
    })();
    $: confirmBtnClickBlocked =
        !$isLogin && phoneDigits.length > 0 && !isPhoneValid;

    $: endBtnErrorStyle =
        loginType === 'E' && !$isLogin && !confirmBtnHighlighted;

    $: if (loginType === 'E') {
        dispatch('inputErrorChange', endBtnErrorStyle);
    }

    const loginFlow = () => (useCardLoginFifa ? cardLoginFlow : legacyLoginFlow);

    const getLoginModuleEnum = () =>
        useCardLoginFifa ? CARD_LOGIN_MODULE_ENUM : LEGACY_MODULE_ENUM;

    const navigateToLoginFlow = async (
        module: LegacyLoginModuleType | CardLoginModuleType
    ) => {
        loginFlow().prepareLoginFlowNavigation(module, phoneDigits);
        PhoneCell.set(phoneDigits);
        isJumpOneLink.set(true);
        startTime.set(Date.now());

        if (useCardLoginFifa) {
            pageLeave();
        }

        try {
            await replace('/login');
        } catch (error) {
            console.error('[PhoneInput navigateToLoginFlow]', error);
            window.location.hash = '#/login';
        }
    };

    const handleSliderCaptcha = () => {
        genSliderCaptchaApi({
            onReady: () => {},
            onInitFail: () => {},
            onClose: () => {},
            onSuccess: () => {
                trackEvent(PHONE_VERIFY_SW, { result: 1 });
                loginFlow().markCodeSent();
                loginFlow().startCodeCountdown();
                navigateToLoginFlow(getLoginModuleEnum().CODE);
            },
            onResendFail: (payload) => {
                const response = (payload as { payload?: { errno?: number } })
                    ?.payload;

                handlePhoneCheckError(response || { errno: 'request_error' });
            }
        })
            .then((instance) => {
                sliderCaptchaInstance = instance;
            })
            .catch(() => {});
    };

    const PHONE_CHECK_INVALID_TIPS =
        'Número de celular inválido. Intenta de nuevo.';
    const PHONE_CHECK_RATE_LIMIT_TIPS =
        'Demasiados intentos. Espera un poco antes de volver a intentar.';
    const PHONE_CHECK_GENERIC_TIPS =
        'Error al obtener el código de verificación. Inténtalo de nuevo más tarde.';

    const resolvePassportErrno = (errno?: number | string) => {
        const parsed = Number(errno);

        return Number.isFinite(parsed) ? parsed : errno;
    };

    const handlePhoneCheckError = (response: {
        errno?: number | string;
        error?: string;
    }): boolean => {
        trackEvent(PHONE_VERIFY_SW, {
            result: 0,
            error_code: response?.errno || 'request_error',
            error_msg: response?.error || 'request_error'
        });

        const errno = resolvePassportErrno(response?.errno);

        if (errno === 41002) {
            navigateToLoginFlow(getLoginModuleEnum().PICTURE);

            return true;
        }

        if (errno === 41027) {
            handleSliderCaptcha();

            return false;
        }

        if (errno === 53001) {
            handleTipsMessage(true, PHONE_CHECK_RATE_LIMIT_TIPS);
            focusPhoneInput();

            return false;
        }

        if (errno === 40001) {
            handleTipsMessage(true, PHONE_CHECK_INVALID_TIPS);
            focusPhoneInput();

            return false;
        }

        handleTipsMessage(true, PHONE_CHECK_GENERIC_TIPS);
        focusPhoneInput();

        return false;
    };

    export const handleConfirmClick = async () => {
        if (buttonLoading || (useCardLoginFifa && $isLoading)) return;

        if ($isLogin && useCardLoginFifa) {
            await handleLandingCtaClick({
                buttonName: 'next',
                extraTrack: {
                    is_phone_input_full: phoneDigits.length === MAX_LENGTH ? 1 : 0
                }
            });

            return;
        }

        trackEvent($isLogin ? LOGIN_STATE_CK : PAGE_CLICK_CK, {
            button_name: 'next',
            is_phone_input_full: phoneDigits.length === MAX_LENGTH ? 1 : 0,
            is_have_check_agreement_btn: 'no_have'
        });

        if ($isLogin) {
            buttonLoading = true;
            await JumpLink(JUMP_TYPE_ENUM.TO_H5CREDITCARD);
            buttonLoading = false;

            return;
        }

        if (phoneDigits.length < MAX_LENGTH || isAllZeroPhone(phoneDigits)) {
            keepConfirmBtnHighlighted = false;
            showInvalidPhoneError();
            focusPhoneInput();

            return;
        }

        initPassportSDK();

        if (!isPassportSdkReady()) {
            console.error('[PhoneInput handleConfirmClick] Passport SDK is not ready');
            showInvalidPhoneError();

            return;
        }

        let enteredLoginFlow = false;

        try {
            buttonLoading = true;
            handleTipsMessage(false, '');
            isJumpOneLink.set(true);
            markPopBegin({ popup_id: POPUP_ID.PASSPORT_POPUP });
            loginFlow().markVerificationCodeLoadingStart();

            await checkPhoneApi(phoneDigits);
            trackEvent(PHONE_VERIFY_SW, { result: 1 });
            loginFlow().markCodeSent();
            loginFlow().startCodeCountdown();
            await navigateToLoginFlow(getLoginModuleEnum().CODE);
            enteredLoginFlow = true;
        } catch (err) {
            enteredLoginFlow = handlePhoneCheckError(getPassportError(err) || {});
        } finally {
            buttonLoading = false;

            if (!enteredLoginFlow) {
                isJumpOneLink.set(false);
            }
        }
    };

    const handleCurpJumpLink = () => {
        pageLeave();
        trackEvent(PAGE_CLICK_CK, {
            button_name: 'view_agreement',
            is_phone_input_full: phoneDigits.length === MAX_LENGTH ? 1 : 0
        });
    };

    const initializePhoneInput = () => {
        if ($isLogin || !phoneInputEl) return;

        const saved = ($PhoneCell || '').replace(/\D/g, '');

        if (!saved) return;

        onPhoneInputFormat(phoneInputEl, saved);
        hasTypedAnyDigit = saved.length > 0;

        const len = phoneInputEl.value.length;

        phoneInputEl.setSelectionRange(len, len);
    };

    onMount(() => {
        handleTipsMessage(false, '');
        tick().then(() => initializePhoneInput());
    });

    $: if ($cookieHeight === 0) {
        cancelCookieScrollAnim();
    }

    $: if ($cookieHeight > 0 && scrollElement) {
        const cookieBarHeight = $cookieHeight;

        requestAnimationFrame(() => {
            try {
                const elementRect = scrollElement.getBoundingClientRect();
                const windowHeight = window.innerHeight;
                const distanceFromBottom = windowHeight - elementRect.bottom;
                const gap = 20;

                if (distanceFromBottom < cookieBarHeight) {
                    scrollByLinear(cookieBarHeight - distanceFromBottom + gap);
                }
            } catch (e) {
                console.log(e);
            }
        });
    }

    onDestroy(() => {
        cancelCookieScrollAnim();
        sliderCaptchaInstance?.destroy?.();
    });
</script>

<div
    class="phone-input-component login-type-{loginType === 'E' ? 'e' : 'default'}"
    class:phone-input-component--ios={loginType === 'E'}
    class:phone-input-component--logged={$isLogin}
>
    {#if !$isLogin}
        <div
            role="none"
            class="phone-input-area"
            class:is-input-error={isInputError}
            on:click={focusPhoneInput}
        >
            <div class="country-flag">
                <img use:webp src={countryIcon} alt="" />
            </div>
            <div class="calling-code">+52</div>
            {#if loginType === 'E'}
                <div class="calling-code-divider" aria-hidden="true"></div>
            {/if}
            <input
                bind:this={phoneInputEl}
                class="phone-input"
                type="tel"
                placeholder="55 1234 5678"
                on:focus={focusPhoneInput}
                on:click|stopPropagation={handlePhoneInputClick}
                on:blur={handleBlurInput}
                on:input={handleInput}
            />
            {#if shouldShowProgress}
                <div
                    class="input-length-show"
                    class:is-input-complete={phoneDigits.length === MAX_LENGTH}
                >
                    <span>{phoneDigits.length}</span>/{MAX_LENGTH}
                </div>
            {/if}
            {#if phoneDigits.length}
                <div
                    class="close-icon"
                    role="none"
                    on:click|stopPropagation={handleEmptyInput}
                >
                    <img
                        use:webp
                        src={loginType === 'E' ? INPUT_CLOSE_ICON : closePhoneIcon}
                        alt=""
                    />
                </div>
            {/if}
        </div>
        {#if loginType === 'E'}
            <div class="input-tips-slot">
                {#if tipsMessage}
                    <div class="input-tips">
                        <div class="tips-message" class:is-input-error={isInputError}>
                            <div
                                class="tips-message__icon"
                                style="background-image: url({isInputError
                                    ? INPUT_TIPS_ERROR_ICON
                                    : INPUT_TIPS_NORMAL_ICON});"
                            ></div>
                            {tipsMessage}
                        </div>
                    </div>
                {/if}
            </div>
        {:else if tipsMessage}
            <div class="input-tips">
                <div class="tips-message" class:is-input-error={isInputError}>
                    <div
                        class="tips-message__icon"
                        style="background-image: url({isInputError
                            ? INPUT_TIPS_ERROR_ICON
                            : INPUT_TIPS_NORMAL_ICON});"
                    ></div>
                    {tipsMessage}
                </div>
            </div>
        {/if}
        {#if loginType === 'E'}
            <div class="policy">
                <a
                    href={CREDIT_CARD_PROTOCOL}
                    class="policy__full-link"
                    rel="noopener"
                    target="_blank"
                    tabindex="0"
                    on:click={handleCurpJumpLink}
                >
                    <span class="policy__text">Al continuar, aceptas los </span>
                    <span class="policy__link-text"
                        >Términos y Condiciones y el Aviso de Privacidad.</span
                    >
                </a>
            </div>
        {/if}
    {/if}
    <div
        bind:this={confirmButton}
        class="confirm-btn"
        role="none"
        on:click={handleConfirmClick}
        class:disabled={!confirmBtnHighlighted}
        class:click-blocked={confirmBtnClickBlocked}
        class:loading={buttonLoading || (useCardLoginFifa && $isLoading)}
        class:confirm-btn--input-error={endBtnErrorStyle}
    >
        <div class="confirm-btn-text">
            {#if buttonLoading || (useCardLoginFifa && $isLoading)}
                <div class="loading-dots">
                    <span class="dot"></span>
                    <span class="dot"></span>
                    <span class="dot"></span>
                </div>
            {:else}
                Continuar
            {/if}
        </div>
    </div>
    {#if !$isLogin && loginType !== 'E'}
        <div class="policy">
            <a
                href={CREDIT_CARD_PROTOCOL}
                class="policy__full-link"
                rel="noopener"
                target="_blank"
                tabindex="0"
                on:click={handleCurpJumpLink}
            >
                <span class="policy__text">Al continuar, aceptas los </span>
                <span class="policy__link-text"
                    >Términos y Condiciones y el Aviso de Privacidad.</span
                >
            </a>
        </div>
    {/if}
    {#if !$isLogin}
        <div class="scroll-element" bind:this={scrollElement}></div>
    {/if}
</div>

<style lang="scss">
    .phone-input-component {
        padding: 40px 40px 60px 40px;

        .phone-input-area {
            margin-bottom: 28px;
            position: relative;
            height: 104px;
            display: flex;
            align-items: center;
            background: #f2f2f5;
            border-radius: 20px;
            padding: 0 20px 0 20px;
            .country-flag {
                width: 52px;
                height: 32px;
                flex-shrink: 0;
                img {
                    width: 100%;
                    height: 100%;
                    display: block;
                }
            }

            .calling-code {
                font-family: DiDiSans-Pro-Bold;
                font-size: 40px;
                font-weight: bold;
                line-height: 56px;
                letter-spacing: 0px;
                margin: 0 21px 0 11px;
                flex-shrink: 0;
                color: #0b0b0d;
            }

            .phone-input {
                width: 300px;
                height: 40px;
                margin-right: 140px;
                flex: 1;
                border: none;
                outline: none;
                background: none;
                font-family: DiDiSans-Pro-Bold;
                font-weight: bold;
                font-size: 40px;
                color: #0c0c0d;
                &::placeholder {
                    color: #bababf;
                }
            }

            &.is-input-error {
                border: 2px solid #f2183d;
                .phone-input {
                    color: #f2183d;
                }
            }

            .close-icon {
                width: 40px;
                height: 40px;
                position: absolute;
                right: 20px;
                top: 50%;
                transform: translateY(-50%);
                img {
                    width: 100%;
                    height: 100%;
                    display: block;
                }
            }

            .input-length-show {
                position: absolute;
                right: 80px;
                top: 50%;
                transform: translateY(-50%);
                font-family: DiDiSans-Pro-Normal;
                font-size: 28px;
                font-weight: normal;
                line-height: 36px;
                letter-spacing: 0px;
                color: #0b0b0d;
                span {
                    color: #000000;
                }
                &.is-input-complete {
                    span {
                        color: #000000;
                    }
                }
            }
        }

        .input-tips {
            display: flex;
            height: 36px;
            position: relative;
            margin-bottom: 22px;

            .tips-message {
                font-family: DiDiSans-Pro-Regular;
                font-size: 24px;
                font-weight: normal;
                line-height: 32px;
                letter-spacing: 0px;
                color: #0c0c0d;
                display: flex;
                align-items: flex-start;
                margin-right: 0;
                &.is-input-error {
                    color: #f2183d;
                }

                .tips-message__icon {
                    width: 28px;
                    height: 28px;
                    margin-right: 7px;
                    flex-shrink: 0;
                    background-size: 100% 100%;
                    background-repeat: no-repeat;
                }
            }
        }

        .confirm-btn {
            position: relative;
            width: 100%;
            height: 104px;
            border-radius: 20px;
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 6;
            padding: 28px 32px;
            background: #ff5f14;

            .confirm-btn-text {
                display: flex;
                align-items: center;
                font-family: DiDiSans-Pro-ExtraBold;
                font-size: 40px;
                font-weight: 800;
                line-height: 64px;
                letter-spacing: 0px;
                color: #fff;
            }
            .loading-dots {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 0.1rem;

                .dot {
                    width: 0.16rem;
                    height: 0.16rem;
                    background: #ffffff;
                    border-radius: 50%;
                    opacity: 0.5;
                    transform: translateZ(0);

                    @for $i from 1 through 3 {
                        &:nth-child(#{$i}) {
                            animation: opacity-change 0.9s ease-in-out infinite;
                            animation-delay: #{($i - 1) * 0.3}s;
                        }
                    }
                }
            }
            @keyframes opacity-change {
                0%,
                100% {
                    opacity: 0.5;
                }
                50% {
                    opacity: 1;
                }
            }

            &.disabled {
                background: #f2f2f5;
                .confirm-btn-text {
                    color: #bababf;
                }
            }

            &.click-blocked {
                pointer-events: none;
            }
        }

        .policy {
            font-family: DiDiSans-Pro-Normal;
            margin-top: 20px;
            font-size: 24px;
            line-height: 32px;
            letter-spacing: 0px;
            margin-left: 16px;
            color: #0b0b0d;

            a {
                text-decoration: none;
            }

            .policy__full-link {
                display: inline-block;
                padding: 8px 0;
            }

            .policy__text {
                color: #999999;
            }

            .policy__link-text {
                color: #101010;
                font-family: DiDiSans-Pro-Regular;
            }
        }
    }

    .phone-input-component--logged {
        padding-top: 60px;
    }

    .phone-input-component.login-type-e {
        padding: 0;

        &.phone-input-component--logged {
            padding-top: 0;
        }

        .phone-input-area {
            position: relative;
            margin-bottom: 0;
            height: 1rem;
            padding: 0 0.4rem 0 0.32rem;
            border-radius: 0.28rem;
            border: 2px solid rgba(255, 186, 153, 0.5);
            background: rgba(59, 42, 24, 0.46);
            overflow: visible;
        }

        @supports (color: color(display-p3 1 1 1)) {
            .phone-input-area {
                border-color: color(display-p3 1 0.743 0.6226 / 0.5);
                background: color(display-p3 0.2229 0.1685 0.1062 / 0.46);
            }
        }

        .phone-input-area.is-input-error {
            border-color: #f2183d;
        }

        .input-tips-slot {
            position: relative;
            height: 0.6rem;
            flex-shrink: 0;
        }

        .input-tips {
            position: absolute;
            top: 0.14rem;
            left: 0;
            right: 0;
            display: flex;
            margin-top: 0;
            margin-bottom: 0;

            .tips-message.is-input-error {
                align-items: center;
                font-family: DiDiSans-Pro-Regular;
                font-size: 0.24rem;
                font-weight: 400;
                line-height: 0.32rem;
                color: #ff0023;

                .tips-message__icon {
                    width: 0.24rem;
                    height: 0.24rem;
                }
            }
        }

        @supports (color: color(display-p3 1 1 1)) {
            .input-tips .tips-message.is-input-error {
                color: color(display-p3 0.949 0.0941 0.2392);
            }
        }

        .calling-code {
            margin: 0 0.24rem 0 0.1rem;
            color: #ffffff;
        }

        .calling-code-divider {
            width: 2px;
            height: 0.96rem;
            flex-shrink: 0;
            background: rgba(255, 186, 153, 0.5);
        }

        @supports (color: color(display-p3 1 1 1)) {
            .calling-code-divider {
                background: color(display-p3 1 0.743 0.6226 / 0.5);
            }
        }

        .phone-input-area.is-input-error .calling-code-divider {
            background: #f2183d;
        }

        .phone-input {
            flex: 1;
            min-width: 0;
            width: auto;
            height: auto;
            margin-right: 0;
            margin-left: 0.24rem;
            font-size: 32px;
            line-height: 32px;
            color: #ffffff;
            caret-color: #ffffff;

            &::placeholder {
                color: rgba(255, 255, 255, 0.5);
            }

            &:focus {
                color: #ffffff;

                &::placeholder {
                    color: rgba(255, 255, 255, 0.5);
                }
            }
        }

        /* end 页：iOS / Android 统一 32px */
        .calling-code {
            font-size: 32px;
            line-height: 32px;
        }

        .phone-input-area.is-input-error .phone-input {
            color: #f2183d;
            caret-color: #f2183d;
        }

        .phone-input-area .input-length-show,
        .phone-input-area .input-length-show span,
        .phone-input-area .input-length-show.is-input-complete,
        .phone-input-area .input-length-show.is-input-complete span {
            color: #ffffff;
        }

        .phone-input-area .input-length-show {
            position: absolute;
            top: 50%;
            right: 0.8rem;
            transform: translateY(-50%);
            z-index: 2;
            font-size: 0.28rem;
            line-height: 0.36rem;
            pointer-events: none;
        }

        .close-icon {
            z-index: 3;
            right: 0.2rem;
            width: 0.4rem;
            height: 0.4rem;
        }

        .confirm-btn {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 1.04rem;
            margin-top: 0;
            border-radius: var(--Gone-radius-M, 0.32rem);
            padding: 0;
            background: var(---orange-6, #ff3800);

            &.disabled {
                background: #644433;

                .confirm-btn-text {
                    color: #937c70;
                }
            }

            &.confirm-btn--input-error,
            &.disabled.confirm-btn--input-error {
                background: #644433;

                .confirm-btn-text {
                    color: #937c70;
                }
            }
        }

        @supports (color: color(display-p3 1 1 1)) {
            .confirm-btn {
                background: var(
                    ---orange-6,
                    color(display-p3 1 0.3725 0.0784)
                );

                &.disabled,
                &.confirm-btn--input-error,
                &.disabled.confirm-btn--input-error {
                    background: color(display-p3 0.3721 0.2723 0.21);

                    .confirm-btn-text {
                        color: color(display-p3 0.5608 0.4902 0.4471);
                    }
                }
            }
        }

        .confirm-btn-text {
            display: flex;
            justify-content: center;
            align-items: center;
            font-family: DiDiSans-Pro-Bold;
            font-weight: 700;
            font-size: 0.4rem;
            line-height: 0.56rem;
            letter-spacing: 0;
            text-align: center;
        }

        .policy {
            margin-top: 0.16rem;
            margin-bottom: 0.24rem;
            margin-left: 0;
            padding-left: 0.04rem;
            padding-right: 0.04rem;
            box-sizing: border-box;
            font-family: DiDiSans-Pro-Regular;
            font-size: 0.24rem;
            font-weight: 400;
            line-height: 0.32rem;
            letter-spacing: 0;
            color: rgba(255, 255, 255, 0.65);

            a {
                text-decoration: none;
            }

            .policy__full-link {
                display: inline;
                padding: 0;
            }

            .policy__text {
                color: rgba(255, 255, 255, 0.65);
            }

            .policy__link-text {
                color: #ffffff;
                font-family: DiDiSans-Pro-Regular;
                text-decoration: none;
            }
        }

        .input-tips-slot + .policy {
            margin-top: 0;
        }

        @supports (color: color(display-p3 1 1 1)) {
            .policy {
                color: color(display-p3 1 1 1 / 0.65);

                .policy__text {
                    color: color(display-p3 1 1 1 / 0.65);
                }

                .policy__link-text {
                    color: color(display-p3 1 1 1);
                }
            }
        }
    }

    /* E 模块：输入框右侧字数（含满位态）保持白色，覆盖 default 深色规则 */
    .phone-input-component.login-type-e .input-length-show,
    .phone-input-component.login-type-e .input-length-show span,
    .phone-input-component.login-type-e .input-length-show.is-input-complete,
    .phone-input-component.login-type-e .input-length-show.is-input-complete span {
        color: #ffffff;
    }

    @supports (color: color(display-p3 1 1 1)) {
        .phone-input-component.login-type-e .input-length-show,
        .phone-input-component.login-type-e .input-length-show span,
        .phone-input-component.login-type-e .input-length-show.is-input-complete,
        .phone-input-component.login-type-e .input-length-show.is-input-complete span {
            color: color(display-p3 1 1 1);
        }
    }
</style>
